import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { adminAxiosInstance, setAdminAccessToken } from "../api/adminAxiosInstance";
import { clearOpsOrdersCache } from "../api/adminOpsOrders";
import { clearRegisteredUsersCache } from "../api/adminMarketingUsers";
import { clearMarketingFarmsCache } from "../api/adminMarketingFarms";
import { clearAdminMarketingOverviewCache } from "../api/adminMarketingOverview";
import { clearAdminOverviewStatsCache } from "../api/adminOverviewStats";
import { PageLoader } from "../components/loaders";

export const ADMIN_ROLES = {
  OPS: "ops_admin",
  MARKETING: "marketing_admin",
  SUPER: "admin",
};

export const ADMIN_PERMISSIONS = Object.freeze({
  OVERALL: Object.freeze([ADMIN_ROLES.OPS, ADMIN_ROLES.MARKETING, ADMIN_ROLES.SUPER]),
  OPERATIONS: Object.freeze([ADMIN_ROLES.OPS, ADMIN_ROLES.SUPER]),
  MARKETING: Object.freeze([ADMIN_ROLES.MARKETING, ADMIN_ROLES.SUPER]),
  CUSTOMERS: Object.freeze([ADMIN_ROLES.MARKETING, ADMIN_ROLES.SUPER]),
});

// ['admin', 'ops_admin', 'marketing_admin']

const roleAliases = {
  ops: ADMIN_ROLES.OPS,
  operations: ADMIN_ROLES.OPS,
  ops_admin: ADMIN_ROLES.OPS,
  operations_admin: ADMIN_ROLES.OPS,
  marketing: ADMIN_ROLES.MARKETING,
  marketing_admin: ADMIN_ROLES.MARKETING,
  super: ADMIN_ROLES.SUPER,
  superadmin: ADMIN_ROLES.SUPER,
  super_admin: ADMIN_ROLES.SUPER,
  admin: ADMIN_ROLES.SUPER,
  administrator: ADMIN_ROLES.SUPER,
};

const ADMIN_SESSION_KEY = "agrogrid_admin_session";
export const ADMIN_PORTAL_PATH = "/admin/internal";

let adminRefreshPromise = null;
const isAdminUserMissing = (payload) => payload?.status === false && String(payload?.message || "").trim().toLowerCase() === "user not found!";

const requestAdminTokenRefresh = (token) => {
  if (!token) return Promise.resolve(null);
  if (!adminRefreshPromise) {
    adminRefreshPromise = adminAxiosInstance
      .post("/auth/token/refresh", { token })
      .then(({ data }) => data)
      .finally(() => {
        adminRefreshPromise = null;
      });
  }
  return adminRefreshPromise;
};

const readAdminSession = () => {
  if (typeof window === "undefined") return null;
  try {
    const session = JSON.parse(window.sessionStorage.getItem(ADMIN_SESSION_KEY));
    if (!session?.accessToken || !session?.refreshToken || !session?.user || !session?.role) return null;
    const normalizedRole = roleAliases[String(session.role).toLowerCase().replace(/[\s-]+/g, "_")];
    if (!normalizedRole) {
      window.sessionStorage.removeItem(ADMIN_SESSION_KEY);
      return null;
    }
    if (session.refreshTokenValidity && Number(session.refreshTokenValidity) <= Date.now()) {
      window.sessionStorage.removeItem(ADMIN_SESSION_KEY);
      return null;
    }
    return { ...session, role: normalizedRole, user: { ...session.user, role: normalizedRole } };
  } catch {
    window.sessionStorage.removeItem(ADMIN_SESSION_KEY);
    return null;
  }
};

const writeAdminSession = (session) => {
  if (typeof window === "undefined") return;
  if (session) window.sessionStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(session));
  else window.sessionStorage.removeItem(ADMIN_SESSION_KEY);
};

export const normalizeAdminRole = (user) => {
  const roles = user?.roles;
  const candidate =
    user?.admin_role ?? user?.adminRole ?? user?.role ?? user?.user_role ??
    user?.userType ?? user?.user_type ?? user?.account_type ??
    (Array.isArray(roles) ? roles.find((item) => {
      const value = typeof item === "string" ? item : item?.name;
      return roleAliases[String(value).toLowerCase().replace(/[\s-]+/g, "_")];
    }) : roles);

  const value = typeof candidate === "string" ? candidate : candidate?.name;
  return value ? roleAliases[String(value).toLowerCase().replace(/[\s-]+/g, "_")] ?? null : null;
};

export const getDefaultAdminPath = (role) => {
  if (role === ADMIN_ROLES.OPS) return `${ADMIN_PORTAL_PATH}/dashboard/operations`;
  if (role === ADMIN_ROLES.MARKETING) return `${ADMIN_PORTAL_PATH}/dashboard/marketing`;
  return `${ADMIN_PORTAL_PATH}/dashboard/operations`;
};

export const canAdminRoleAccessPath = (role, path = "") => {
  const pathname = String(path).split(/[?#]/, 1)[0];

  if (pathname === ADMIN_PORTAL_PATH || pathname === `${ADMIN_PORTAL_PATH}/dashboard`) return Boolean(role);
  if (pathname.startsWith(`${ADMIN_PORTAL_PATH}/dashboard/operations`)) return ADMIN_PERMISSIONS.OVERALL.includes(role);
  if (pathname.startsWith(`${ADMIN_PORTAL_PATH}/dashboard/marketing`)) return ADMIN_PERMISSIONS.OVERALL.includes(role);
  if (pathname.startsWith(`${ADMIN_PORTAL_PATH}/operations/`) || pathname.startsWith(`${ADMIN_PORTAL_PATH}/resources/`)) {
    return ADMIN_PERMISSIONS.OPERATIONS.includes(role);
  }
  if (pathname.startsWith(`${ADMIN_PORTAL_PATH}/customers/`) || pathname === `${ADMIN_PORTAL_PATH}/buyers` || pathname.startsWith(`${ADMIN_PORTAL_PATH}/buyers/`)) {
    return ADMIN_PERMISSIONS.CUSTOMERS.includes(role);
  }
  if (pathname.startsWith(`${ADMIN_PORTAL_PATH}/marketing/`)) return ADMIN_PERMISSIONS.MARKETING.includes(role);

  return false;
};

export const getAdminRedirectPath = (redirectUri, role) => {
  const isSafeAdminPath = redirectUri?.startsWith(`${ADMIN_PORTAL_PATH}/`)
    && !redirectUri.startsWith("//")
    && !redirectUri.startsWith(`${ADMIN_PORTAL_PATH}/login`);
  return isSafeAdminPath && canAdminRoleAccessPath(role, redirectUri)
    ? redirectUri
    : getDefaultAdminPath(role);
};

export const AdminAuthenticationContext = createContext(null);

export const AdminAuthenticationProvider = () => {
  const [initialSession] = useState(readAdminSession);
  const [user, setUser] = useState(initialSession?.user ?? null);
  const [role, setRole] = useState(initialSession?.role ?? null);
  const [accessTokenValidity, setAccessTokenValidity] = useState(initialSession?.accessTokenValidity ?? null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isInitializing, setIsInitializing] = useState(Boolean(initialSession));
  const sessionRef = useRef(initialSession);
  const refreshTokenRef = useRef(initialSession?.refreshToken ?? null);
  const isTokenValid = !accessTokenValidity || accessTokenValidity > Date.now();
  const isAuthenticated = isTokenValid && !!user && !!normalizeAdminRole({ role });

  const logout = useCallback(() => {
    setAdminAccessToken(null);
    clearOpsOrdersCache();
    clearRegisteredUsersCache();
    clearMarketingFarmsCache();
    clearAdminMarketingOverviewCache();
    clearAdminOverviewStatsCache();
    writeAdminSession(null);
    sessionRef.current = null;
    refreshTokenRef.current = null;
    setUser(null);
    setRole(null);
    setAccessTokenValidity(null);
  }, []);

  const applySession = useCallback((session) => {
    sessionRef.current = session;
    refreshTokenRef.current = session.refreshToken;
    setAdminAccessToken(session.accessToken);
    writeAdminSession(session);
    setUser(session.user);
    setRole(session.role);
    setAccessTokenValidity(session.accessTokenValidity);
  }, []);

  const refreshAccessToken = useCallback(async (tokenOverride = null) => {
    const tokenToUse = tokenOverride || refreshTokenRef.current;
    const currentSession = sessionRef.current;
    if (!tokenToUse || !currentSession) return null;

    setIsRefreshing(true);
    try {
      const data = await requestAdminTokenRefresh(tokenToUse);
      if (!data?.access_token) throw new Error("The token refresh response did not include an access token.");

      // A previous admin may have logged out while this refresh was pending.
      // Never allow that response to overwrite the session created by a later login.
      if (sessionRef.current !== currentSession || refreshTokenRef.current !== tokenToUse) return null;

      const accessValidity = Number(data.access_token_validity);
      const refreshValidity = Number(data.refresh_token_validity);
      const nextSession = {
        ...currentSession,
        accessToken: data.access_token,
        accessTokenValidity: Number.isFinite(accessValidity) && accessValidity > 0 ? accessValidity : null,
        refreshToken: data.refresh_token || tokenToUse,
        refreshTokenValidity: Number.isFinite(refreshValidity) && refreshValidity > 0 ? refreshValidity : currentSession.refreshTokenValidity,
      };
      applySession(nextSession);
      return nextSession.accessToken;
    } catch {
      logout();
      return null;
    } finally {
      setIsRefreshing(false);
    }
  }, [applySession, logout]);

  useLayoutEffect(() => {
    const responseInterceptor = adminAxiosInstance.interceptors.response.use(
      (response) => {
        if (!isAdminUserMissing(response?.data)) return response;
        logout();
        const error = new Error(response.data.message);
        error.response = response;
        return Promise.reject(error);
      },
      async (error) => {
        if (isAdminUserMissing(error.response?.data)) {
          logout();
          return Promise.reject(error);
        }

        const request = error.config;
        if (request?.url?.includes("/auth/token/refresh") || request?._adminRetry) return Promise.reject(error);

        const message = String(error.response?.data?.message || "").toLowerCase();
        const isExpiredToken = message.includes("x-authenticated-user") || message.includes("unauthorized") || message.includes("expired");
        if (error.response?.status === 401 && refreshTokenRef.current && isExpiredToken) {
          request._adminRetry = true;
          const newAccessToken = await refreshAccessToken();
          if (newAccessToken) {
            request.headers = request.headers || {};
            request.headers["x-authenticated-user"] = `Bearer ${newAccessToken}`;
            return adminAxiosInstance(request);
          }
        }

        if (error.response?.status === 401) logout();
        return Promise.reject(error);
      }
    );

    return () => adminAxiosInstance.interceptors.response.eject(responseInterceptor);
  }, [logout, refreshAccessToken]);

  useLayoutEffect(() => {
    if (!initialSession) return undefined;
    let active = true;

    const initializeAdminAuth = async () => {
      setAdminAccessToken(initialSession.accessToken);
      if (initialSession.accessTokenValidity && Number(initialSession.accessTokenValidity) <= Date.now()) {
        await refreshAccessToken(initialSession.refreshToken);
      }
      if (active) setIsInitializing(false);
    };

    initializeAdminAuth();
    return () => { active = false; };
  }, [initialSession, refreshAccessToken]);

  useEffect(() => {
    if (!accessTokenValidity) return undefined;
    let timeout;
    const scheduleExpiry = () => {
      const remaining = accessTokenValidity - Date.now();
      if (remaining <= 0) return refreshAccessToken();
      timeout = window.setTimeout(scheduleExpiry, Math.min(remaining, 2147483647));
    };
    
    scheduleExpiry();
    return () => window.clearTimeout(timeout);
  }, [accessTokenValidity, refreshAccessToken]);

  const login = async ({ username, password }) => {
    setIsLoading(true);
    try {
      const { data } = await adminAxiosInstance.post(
        "/admin/internal/auth/login",
        { username, password }
      );
      const rawRole = data.role ?? data.user?.role;
      const adminRole = normalizeAdminRole({ role: rawRole });

      if (!data.status || !data.access_token || !adminRole) {
        logout();
        return { success: false, message: data.message || "This account does not have admin access." };
      }

      const adminUser = { ...data.user, id: data.user?.id ?? data.user_id, role: adminRole };
      const accessValidity = Number(data.access_token_validity);
      const refreshValidity = Number(data.refresh_token_validity);
      const session = {
        user: adminUser,
        role: adminRole,
        accessToken: data.access_token,
        accessTokenValidity: Number.isFinite(accessValidity) && accessValidity > 0 ? accessValidity : null,
        refreshToken: data.refresh_token || null,
        refreshTokenValidity: Number.isFinite(refreshValidity) && refreshValidity > 0 ? refreshValidity : null,
      };

      applySession(session);

      return { success: true, message: data.message, data: { ...data, user: adminUser, role: adminRole, backendRole: rawRole } };
    } catch (error) {
      logout();
      return {
        success: false,
        message: error.response?.data?.message || error.message || "We couldn't sign you in. Please try again.",
      };
    } finally {
      setIsLoading(false);
    }
  };

  if (isInitializing) return <PageLoader />;

  return (
    <AdminAuthenticationContext.Provider value={{ user, role, isLoading, isRefreshing, isAuthenticated, login, logout, refreshAccessToken }}>
      <Outlet />
    </AdminAuthenticationContext.Provider>
  );
};

export const ProtectedAdminRoute = () => {
  const { isAuthenticated, role } = useAdminAuth();
  const location = useLocation();
  const redirectUri = `${location.pathname}${location.search}${location.hash}`;
  if (!isAuthenticated) return <Navigate to={`${ADMIN_PORTAL_PATH}/login?redirect_uri=${encodeURIComponent(redirectUri)}`} replace />;

  return canAdminRoleAccessPath(role, location.pathname)
    ? <Outlet />
    : <Navigate to={getDefaultAdminPath(role)} replace />;
};

export const AdminLoginRoute = () => {
  const { isAuthenticated, role } = useAdminAuth();
  const location = useLocation();
  const redirectUri = new URLSearchParams(location.search).get("redirect_uri");
  return isAuthenticated ? <Navigate to={getAdminRedirectPath(redirectUri, role)} replace /> : <Outlet />;
};

export const RequireAdminRole = ({ allowedRoles }) => {
  const { role } = useAdminAuth();
  return Array.isArray(allowedRoles) && allowedRoles.includes(role)
    ? <Outlet />
    : <Navigate to={getDefaultAdminPath(role)} replace />;
};

export const AdminDashboardRedirect = () => {
  const { role } = useAdminAuth();
  return <Navigate to={getDefaultAdminPath(role)} replace />;
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthenticationContext);
  if (!context) throw new Error("useAdminAuth must be used inside AdminAuthenticationProvider");
  return context;
};
